import { useLayoutEffect, useRef, useState } from "react";

import { formatCalendarDate } from "../../../Utils/helper";
import { parseChatTimestamp } from "../../../Utils/chatMessages.mjs";
import { useDocumentModal } from "../../common/CustomModal/CustomModalContext";
import incidentIcon from "../../../assets/images/incident.svg";
import bdayIcon from "../../../assets/images/birthdayicon.svg";

import avatarImage from "./../../../assets/images/avatar.svg";

const MessageBubble = ({ message, isOwn }) => {
  const { showDocumentModal } = useDocumentModal();
  const rowRef = useRef(null);
  const bubbleRef = useRef(null);
  const [bubbleWidth, setBubbleWidth] = useState(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const bubble = bubbleRef.current;
    const container = row.parentElement;
    let disposed = false;

    const measureBubble = () => {
      if (disposed) return;
      const time = row.querySelector(".chat-message-time");
      const inlineTimeSpace = time.getBoundingClientRect().width + 5;
      const stackTime = container.clientWidth - inlineTimeSpace < 80;
      // Keep the message beside its sender even when its timestamp needs a new line.
      row.classList.toggle("chat-message-row-stacked-time", stackTime);
      const timeSpace = stackTime ? 0 : inlineTimeSpace;
      row.style.setProperty("--chat-time-space", `${timeSpace}px`);
      const availableWidth = container.clientWidth - timeSpace;
      if (availableWidth <= 0) return;

      // Measure balanced lines at the available width, then trim unused space.
      const probe = bubble.cloneNode(true);
      Object.assign(probe.style, {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        width: "fit-content",
        maxWidth: `${availableWidth}px`,
      });
      probe.setAttribute("aria-hidden", "true");
      row.appendChild(probe);
      const text = probe.querySelector(".chat-message-text");
      const range = document.createRange();
      range.selectNodeContents(text);
      const lines = new Map();
      for (const rect of range.getClientRects()) {
        const top = Math.round(rect.top);
        const line = lines.get(top);
        lines.set(top, {
          left: Math.min(line?.left ?? rect.left, rect.left),
          right: Math.max(line?.right ?? rect.right, rect.right),
        });
      }
      const lineWidth = Math.max(
        0,
        ...Array.from(lines.values(), (line) => line.right - line.left)
      );
      const extraWidth = probe.getBoundingClientRect().width - text.getBoundingClientRect().width;
      probe.remove();
      let contextWidth = 0;
      const context = row.querySelector(".chat-message-context-tab");
      if (context) {
        const contextProbe = context.cloneNode(true);
        Object.assign(contextProbe.style, {
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          width: "max-content",
          maxWidth: `${availableWidth}px`,
        });
        contextProbe.setAttribute("aria-hidden", "true");
        row.appendChild(contextProbe);
        contextWidth = contextProbe.getBoundingClientRect().width;
        contextProbe.remove();
      }
      const width = Math.min(
        availableWidth,
        Math.max(Math.ceil(lineWidth + extraWidth), contextWidth)
      );
      setBubbleWidth(width);
    };

    measureBubble();
    const observer = new ResizeObserver(measureBubble);
    observer.observe(container);
    document.fonts?.ready.then(measureBubble);
    return () => {
      disposed = true;
      observer.disconnect();
    };
  }, [message, isOwn]);

  const contextData = message?.context_data;

  const formatBubbleTime = (timestamp) => {
    const date = parseChatTimestamp(timestamp);
    if (!date) return "";

    return date
      .toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase(); // Add this to make AM/PM lowercase
  };

  const getContextInfo = () => {
    const client = contextData?.client;
    const kase = contextData?.case;

    const nameFromClient =
      client?.first_name || client?.last_name
        ? `${client?.last_name ?? ""} ${client?.first_name ?? ""}`.trim()
        : "";

    return {
      name: nameFromClient || message?.name || "",
      avatar: client?.avatar_url || null,
      birthday: formatCalendarDate(client?.birthday ?? message?.birthday),
      caseType: kase?.case_type?.name ?? message?.caseTypeName ?? "",
      caseIcon: kase?.case_type?.icon_url ?? message?.caseTypeIcon ?? "",
      incidentDate: formatCalendarDate(kase?.incident_date ?? message?.incident_date),
    };
  };
  const handleDocumentClick = (event) => {
    const target = event.target.closest("[data-document-modal]");
    if (!target) return;

    event.preventDefault();

    const documentUrl = target.getAttribute("data-document-url");
    const urlObj = new URL(documentUrl);
    const params = new URLSearchParams(urlObj.search);

    const file = params.get("file");
    const documentURI = documentUrl;
    const documentData = params.get("data");
    let decodedDocumentData = documentData;
    if (documentData) {
      try {
        decodedDocumentData = decodeURIComponent(documentData);
        if (decodedDocumentData.startsWith("{") || decodedDocumentData.startsWith("[")) {
          decodedDocumentData = JSON.parse(decodedDocumentData);
        }
      } catch (e) {
        console.error("Error decoding documentData:", e.message);
      }
    }
    if (file === "document") {
      showDocumentModal(
        file,
        `${documentURI}&&thread_id=${message?.thread_id}`,
        decodedDocumentData
      );
    } else {
      showDocumentModal(
        file,
        `${documentURI}&&thread_id=${message?.thread_id}`,
        decodedDocumentData,
        decodedDocumentData,
        () => {},
        decodedDocumentData?.case_thumbnail
      );
    }
  };

  // Process message content for HTML rendering
  const processMessageContent = (content) => {
    return content
      .replace(/\n/g, "<br>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>");
  };

  const getStatusIcon = () => {
    if (!isOwn) return null; // Only show status for own messages

    const iconStyle = {
      width: "14px",
      height: "14px",
      marginLeft: "5px",
    };

    switch (message.status) {
      case "sending":
        return (
          <div style={iconStyle} className="text-muted">
            <div
              style={{
                width: "10px",
                height: "10px",
                border: "1.5px solid rgba(255,255,255,0.3)",
                borderTop: "1.5px solid rgba(255,255,255,0.8)",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        );
      case "sent":
        return (
          <svg
            style={{ ...iconStyle, color: "rgba(255,255,255,0.8)" }}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        );
      case "delivered":
        return (
          <div style={{ display: "flex", gap: "1px", marginLeft: "6px" }}>
            <svg
              style={{ width: "12px", height: "12px", color: "rgba(255,255,255,0.8)" }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <svg
              style={{
                width: "12px",
                height: "12px",
                color: "rgba(255,255,255,0.8)",
                marginLeft: "-4px",
              }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case "read":
        return (
          <div style={{ display: "flex", gap: "1px", marginLeft: "6px" }}>
            <svg
              style={{ width: "12px", height: "12px", color: "#10b981" }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
            <svg
              style={{ width: "12px", height: "12px", color: "#10b981", marginLeft: "-4px" }}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case "failed":
        return (
          <svg
            style={{ ...iconStyle, color: "#ef4444" }}
            fill="currentColor"
            viewBox="0 0 20 20"
            title="Failed to send"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getBubbleStyle = () => {
    const baseStyle = {
      padding: "4px 8px",
      borderRadius: "7px",
      width: bubbleWidth === null ? "fit-content" : `${bubbleWidth}px`,
      wordWrap: "break-word",
      whiteSpace: "pre-wrap",
      fontSize: "14px",
      lineHeight: "1.2",
      maxWidth: "100%",
      opacity: message.status === "sending" ? 0.7 : 1,
      position: "relative",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "flex-end",
      textAlign: "left",
      // height: "25px",
      minHeight: "25px",
    };

    if (isOwn) {
      return {
        ...baseStyle,
        backgroundColor: "var(--primary, #3b82f6)",
        color: "white",
      };
    } else {
      return {
        ...baseStyle,
        backgroundColor: "white",
        color: "#1f2937",
        border: "1px solid #e5e7eb",
      };
    }
  };

  const renderContextHeader = () => {
    const info = getContextInfo();
    return (
      <div
        className="chat-message-context-tab"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          flexWrap: "wrap",
          padding: "4px 8px 14px",
          background: "var(--primary-10)",
          borderRadius: "7px 7px 0 0",
          fontSize: "13px",
          fontWeight: 600,
          color: "#111827",
          width: "100%",
          maxWidth: "100%",
          justifyContent: "center",
        }}
      >
        <span className="text-primary" style={{ fontWeight: 700 }}>
          re:
        </span>

        {info.name && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: isOwn ? "black" : "black",
              fontWeight: 600,
              marginRight: "10px",
            }}
            className=""
          >
            <img
              src={info.avatar ?? avatarImage}
              className="ic-15 d-flex align-items-center justify-content-center"
              style={{
                borderRadius: "50%",
                objectFit: "cover",
                background: "var(--primary-30)",
              }}
            />
            {info.name}
          </span>
        )}

        {info.birthday && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: isOwn ? "black" : "black",
              fontWeight: 600,
              marginRight: "10px",
            }}
          >
            <img
              src={bdayIcon}
              className="ic-15 d-flex align-items-center justify-content-center"
            />
            {info.birthday}
          </span>
        )}

        {(info.caseIcon || info.caseType) && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: isOwn ? "black" : "black",
              fontWeight: 600,
              marginRight: "10px",
            }}
          >
            {info.caseIcon && <img src={info.caseIcon} alt="" style={{ width: 15, height: 15 }} />}
            {info.caseType}
          </span>
        )}

        {info.incidentDate && (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: isOwn ? "black" : "black",
              fontWeight: 600,
            }}
          >
            <img
              src={incidentIcon}
              className="ic-15 d-flex align-items-center justify-content-center"
            />
            {info.incidentDate}
          </span>
        )}
      </div>
    );
  };
  return (
    <div
      className={`chat-message-row ${isOwn ? "chat-message-row-own" : "chat-message-row-received"}`}
      ref={rowRef}
      onClick={handleDocumentClick}
    >
      <div className="chat-message-main">
        {/* change `contextData &&` to `contextData && isFirstInGroup &&` to show once per group */}
        {contextData && <div className="chat-message-context-slot">{renderContextHeader()}</div>}

        <div className="chat-message-content-row">
          <div className="chat-message-bubble" ref={bubbleRef} style={getBubbleStyle()}>
            <div
              className="chat-message-text"
              style={{ marginBottom: "0px" }}
              dangerouslySetInnerHTML={{
                __html: processMessageContent(message.content),
              }}
            />

            {/* Time and status inside the bubble */}
            <div
              style={{
                fontSize: "11px",
                color: isOwn ? "rgba(255,255,255,0.8)" : "#6b7280",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                marginTop: "0px",
                gap: "0px",
              }}
            >
              {isOwn && getStatusIcon()}
            </div>
          </div>
          <div
            className="chat-message-time"
            style={{
              fontSize: "14px",
              color: isOwn ? "#6b7280" : "#6b7280",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              marginTop: "0px",
              gap: "0px",
              whiteSpace: "nowrap",
              minHeight: "25px",
            }}
          >
            {message.status !== "sending" && formatBubbleTime(message.created_at) && (
              <span>{formatBubbleTime(message.created_at)}</span>
            )}
            {/* {isOwn && getStatusIcon()} */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
